import { User, Address } from "kmstack-shared";
import { PlanType as SubscriptionType } from "kmstack-shared/core";

export enum ETitle {
    SIGNUP = 'Create your Kmstack workspace',
    LOGIN = 'Sign in to Kmstack',
    RESET = 'Recover account access',
    TFA = 'Two-Factor Authentication',
    LINK_ACCOUNT = 'Link Account'
}
export enum ETagLines {
    LOGIN = 'Access payroll, invoicing, banking, and compliance controls from one secure workspace.',
    SIGNUP = 'Set up a finance workspace built for multi-organization operations.',
    RESET = 'Enter your email and we will send a secure reset link.',
    TFA = 'Verify your identity to continue securely.'
}

export enum AuthProviderEnum {
    KMSTACK = 'kmstack',
    GOOGLE = 'google',
    APPLEID = 'apple',
    MICROSOFT = 'microsoft'
}


export interface TitleNTagLine {
    title: ETitle,
    tagLine: ETagLines
}

export interface LoginForm {
    email: string;
    password: string;
    ip?: string;
}

export interface IVerificationForm {
    id: string;
    code: string[];
}
export interface ResetForm {
    code: any;
    email: string;
    password: string;
    backupCode: string;
    newPassword: string;
    securityAnswer: string;
}


export type Dictionary = {
    [key: string]: any
};
export type APIStatus =
    | 'loading'
    | 'valid'
    | 'expired'
    | 'invalid'
    | 'error'
    | 'submitting'
    | 'linked'


export type AuthProviderEnumType = AuthProviderEnum.KMSTACK | AuthProviderEnum.GOOGLE | AuthProviderEnum.APPLEID | AuthProviderEnum.MICROSOFT;

export interface ProvincesOrStates {
    name: string;
    stateCode: string;
    code: string;
}

export interface OnBoardCountry {
    name: string;
    code: string;
}

/**
 * Represents the status of a user account.
 */
export enum AccountStatus {
    INACTIVE,
    ACTIVE,
    PWS_RESET,
    LOCKED,
    SET_FOR_DELETION,
    DELETED,
    PENDING_VERIFICATION,
    SUSPENDED,
    DISABLED,
    EXPIRED,
    BANNED,
    MIGRATED,
    DEACTIVATED,
    DELETE_ACCOUNT_REQUEST
}

/** Enum representing various currencies. */
export enum Currency {
    USD = "USD",
    EUR = "EUR",
    GBP = "GBP",
    JPY = "JPY",
    AUD = "AUD",
    CAD = "CAD",
    CHF = "CHF",
    CNY = "CNY",
    INR = "INR",
    SEK = "SEK",
    NZD = "NZD",
    ZAR = "ZAR",
    NOK = "NOK",
    HUF = "HUF",
    MXN = "MXN"
}


/**
 * Represents available billing cycles for subscriptions.
 */
export enum BillingCycle {
    MONTHLY = 'Monthly',
    YEARLY = 'Yearly',
}


/**
 * Represents the status of a subscription.
 */
export enum SubscriptionStatus {
    ACTIVE = 'Active',
    CANCELLED = 'Cancelled',
    SUSPENDED = 'Suspended',
    EXPIRED = 'Expired',
    PAST_DUE = 'Past Due',
    TRIAL = 'Trial',
    INCOMPLETE = 'Incomplete'
}

/**
 * Represents the status of a billing transaction.
 */
export enum BillingStatus {
    PAID = 'Paid',
    FAILED = 'Failed',
    PENDING = 'Pending',
    CANCELLED = "Cancelled"
}




// ******************* Billing Enums ******************* 
// frontend models 

/**
 * Represents the address of a dealer or business.
 */
export interface DealerAddress extends Address { }


/**
 * Defines supported payment method types.
 */
export type PaymentMethodType = 'credit_card' | 'bank_transfer' | 'paypal' | 'other';


// export interface PaymentSubmissionDTO {
//     methodType: PaymentMethodType;
//     cardNumber?: string;
//     cardExpiry?: string;
//     cardCVV?: number;
//     cardHolderName: string;
//     cardBrand?: string;
//     bankAccountNumber?: string;
//     bankRoutingNumber?: string;
//     paypalEmail?: string;
//   }

/**
 * Represents stored payment method details.
 */
export interface StoredPaymentMethod {
    /** Type of payment method used (e.g., credit card, bank transfer). */
    methodType: PaymentMethodType;
    /** Last four digits of the card number for identification. */
    lastFourDigits?: string;
    /** Card number, only used during initial form submission, never persisted. */
    cardNumber?: string;
    /** The brand or type of the card (e.g., Visa, MasterCard). */
    cardBrand?: string;
    /** Card expiry date used for validation. this should NEVER BE PERSISTED OR STORED IN FRONTEND OR BACKEND, IT SHOULD BE PASSED STRAIGHT TO THE PROVIDER */
    cardExpiry?: string;
    /** Card CVV, used for form submission only and never stored. */
    cardCVV?: number;
    /** The Name on card */
    cardHolderName: string
}

/**
 * Represents a payment method including optional bank or PayPal details.
 */
export interface PaymentMethod {
    /** The stored payment method details. */
    storedPaymentMethod: StoredPaymentMethod;
    /** Expiry date of the card. */
    cardExpiry?: string;
    /** Bank account number, if using bank transfer. */
    bankAccountNumber?: string;
    /** Bank routing number, if using bank transfer. */
    bankRoutingNumber?: string;
    /** PayPal email address, if using PayPal. */
    paypalEmail?: string;
}

/**
 * Represents a record of a past billing transaction.
 */
export interface BillingHistory {
    /** Unique identifier for the transaction. */
    id: string;
    /** Date of the transaction. */
    date: Date;
    /** Amount charged during the transaction. */
    amount: number;
    /** Currency used for the transaction. */
    currency: Currency;
    /** Status of the transaction (e.g., Paid, Failed). */
    status: BillingStatus;
    /** Payment method used for the transaction. */
    method: PaymentMethodType;
    /** Additional description about the transaction. */
    description?: string;
    pdfUrl?: string;
}

/**
 * Represents billing method details associated with a subscription.
 */
export interface BillingMethod {
    /** Unique identifier for the billing method. */
    id: string;
    /** Type of subscription (e.g., Pro, Enterprise). */
    type: SubscriptionType;
    /** Price of the subscription. */
    price: number;
    /** Currency used for billing. */
    currency: Currency;
    /** Billing cycle frequency. */
    billingCycle: BillingCycle;
    /** Optional discount applied to the subscription. */
    discount?: number;
    /** Number of trial days offered, if any. */
    trialPeriodDays?: number;
    /** Billing address associated with the method. */
    billingAddress: DealerAddress;
    /** List of available payment methods for billing. */
    paymentMethods: PaymentMethod[];
}

/**
 * Represents a user's subscription plan details.
 */
export interface SubscriptionPlan {
    /** The current subscription plan type. */
    currentPlan: SubscriptionType;
    /** Billing cycle frequency of the subscription. */
    billCycle: BillingCycle;
    /** The next scheduled payment date. */
    nextPayment: Date;
    /** Price of the current subscription plan. */
    price: string | null;
    /** Currency for the current subscription price. */
    currency?: Currency | string;
    /** Features enabled for the current subscription. */
    features?: string[];
    /** The billing method used for the subscription. */
    billingMethod: BillingMethod;
    /** The status of the current subscription. */
    status: SubscriptionStatus;
    /** List of past billing transactions. */
    billingHistory: BillingHistory[];
}



/**
 * Represents the user profile form.
 */
export interface ProfileForm {
    subscriptionPlan: SubscriptionPlan;
    user: User;
}


// ********* END OF BILLING ENUMS *********
